/* screens/analytics/reportTable.jsx — переиспользуемая сортировка + фильтр-по-столбцу для
   таблиц отчётов (ABC, Ликвидность). Клик по заголовку — сорт ▲▼→off; иконка-воронка — поповер
   фильтра по типу столбца (текст / диапазон чисел / галочки значений). Сорт устойчив (стабилен),
   null-значения всегда в конце. Группировка сохраняется — сорт применяется ВНУТРИ групп. */
import React from 'react';
import { Icon } from '../../components/ui/Icon.jsx';
import { Popover } from '../../components/ui/Popover.jsx';
import { parseDecimal } from '../../lib/decimal.js';
import { ColResize } from './ColumnWidths.jsx';

const { useState, useMemo, useCallback } = React;

// col: { key, label, type:'text'|'num'|'enum', get(row), align:'num'|'center'|undefined,
//        enumOptions?:[{value,label}], sortable=true, filterable=true, thClass?, title? }

function _isEmpty(val) {
  if (!val) return true;
  if ('q' in val) return !val.q || !String(val.q).trim();
  if ('min' in val || 'max' in val) {
    const e = (x) => x == null || String(x).trim() === '';
    return e(val.min) && e(val.max);
  }
  if ('set' in val) return !val.set || val.set.length === 0;
  return true;
}

function _match(col, row, val) {
  const v = col.get(row);
  if (col.type === 'text') {
    const q = String(val.q || '').trim().toLowerCase();
    return !q || String(v == null ? '' : v).toLowerCase().includes(q);
  }
  if (col.type === 'num') {
    if (v == null || v === '') return false;               // числовой фильтр исключает пустые
    const n = Number(v);
    const min = _isEmpty({ min: val.min }) ? null : parseDecimal(val.min);
    const max = _isEmpty({ max: val.max }) ? null : parseDecimal(val.max);
    if (min != null && !Number.isNaN(min) && n < min) return false;
    if (max != null && !Number.isNaN(max) && n > max) return false;
    return true;
  }
  if (col.type === 'enum') {
    if (!val.set || !val.set.length) return true;
    return val.set.includes(String(v == null ? '' : v));
  }
  return true;
}

function _cmp(col, a, b) {
  if (col.type === 'num') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), 'ru');
}

export function useReportTable(columns, allRows) {
  const [sort, setSort] = useState(null);                  // {key, dir:'asc'|'desc'} | null
  const [filters, setFilters] = useState({});              // key -> {q}|{min,max}|{set}

  const colByKey = useMemo(() => {
    const m = {}; for (const c of columns) m[c.key] = c; return m;
  }, [columns]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (!s || s.key !== key) ? { key, dir: 'asc' }
      : s.dir === 'asc' ? { key, dir: 'desc' } : null);
  }, []);

  const setFilter = useCallback((key, val) => {
    setFilters((f) => {
      const n = { ...f };
      if (_isEmpty(val)) delete n[key]; else n[key] = val;
      return n;
    });
  }, []);
  const clearAll = useCallback(() => { setFilters({}); setSort(null); }, []);

  const filterRows = useCallback((rows) => {
    const act = Object.entries(filters).filter(([k]) => colByKey[k]);
    if (!act.length) return rows;
    return rows.filter((r) => act.every(([k, v]) => _match(colByKey[k], r, v)));
  }, [filters, colByKey]);

  const sortRows = useCallback((rows) => {
    if (!sort || !colByKey[sort.key]) return rows;
    const col = colByKey[sort.key];
    const dir = sort.dir === 'desc' ? -1 : 1;
    const idx = rows.map((r, i) => [r, i]);
    idx.sort(([a, ai], [b, bi]) => {
      const va = col.get(a), vb = col.get(b);
      const na = va == null || va === '', nb = vb == null || vb === '';
      if (na && nb) return ai - bi;
      if (na) return 1;                                     // пустые всегда в конце
      if (nb) return -1;
      const c = _cmp(col, va, vb);
      return c !== 0 ? c * dir : ai - bi;                   // стабильность
    });
    return idx.map(([r]) => r);
  }, [sort, colByKey]);

  const apply = useCallback((rows) => sortRows(filterRows(rows)), [sortRows, filterRows]);

  const distinctFor = useCallback((col) => {
    if (col.enumOptions) return col.enumOptions;
    const seen = new Set();
    for (const r of (allRows || [])) seen.add(String(col.get(r) == null ? '' : col.get(r)));
    return [...seen].sort((a, b) => a.localeCompare(b, 'ru')).map((v) => ({ value: v, label: v || '—' }));
  }, [allRows]);

  const activeCount = Object.keys(filters).length;
  return { sort, filters, toggleSort, setFilter, clearAll, apply, filterRows, sortRows, distinctFor, activeCount };
}

function ColFilter({ col, value, onChange }) {
  if (col.type === 'text') {
    return (
      <div className="rt-filter">
        <input className="rt-filter__input" type="text" autoFocus placeholder={'Поиск: ' + col.label}
          value={(value && value.q) || ''} onChange={(e) => onChange({ q: e.target.value })} />
        <div className="rt-filter__foot">
          <button type="button" className="rt-filter__clear" onClick={() => onChange(null)}>Очистить</button>
        </div>
      </div>
    );
  }
  if (col.type === 'num') {
    const v = value || {};
    return (
      <div className="rt-filter">
        <div className="rt-filter__range">
          <input className="rt-filter__num" type="text" inputMode="decimal" placeholder="мин"
            value={v.min == null ? '' : v.min} onChange={(e) => onChange({ ...v, min: e.target.value })} />
          <span className="rt-filter__dash">—</span>
          <input className="rt-filter__num" type="text" inputMode="decimal" placeholder="макс"
            value={v.max == null ? '' : v.max} onChange={(e) => onChange({ ...v, max: e.target.value })} />
        </div>
        <div className="rt-filter__foot">
          <button type="button" className="rt-filter__clear" onClick={() => onChange(null)}>Очистить</button>
        </div>
      </div>
    );
  }
  const opts = col._distinct || [];
  const set = new Set((value && value.set) || []);
  const toggle = (val) => {
    const n = new Set(set); n.has(val) ? n.delete(val) : n.add(val);
    onChange(n.size ? { set: [...n] } : null);
  };
  return (
    <div className="rt-filter">
      <div className="rt-filter__list">
        {opts.map((o) => (
          <label key={o.value} className="rt-filter__opt">
            <input type="checkbox" checked={set.has(o.value)} onChange={() => toggle(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
        {!opts.length && <div className="rt-filter__empty">Нет значений</div>}
      </div>
      <div className="rt-filter__foot">
        <button type="button" className="rt-filter__clear" onClick={() => onChange(null)}>Сбросить</button>
      </div>
    </div>
  );
}

// Индикатор сортировки — две симметричные (ровные) стрелки; активная красится акцентом.
export function SortIcon({ dir }) {
  return (
    <svg className="rt-sort" width="9" height="12" viewBox="0 0 9 12" aria-hidden="true">
      <path className={'rt-sort__tri' + (dir === 'asc' ? ' is-on' : '')} d="M4.5 1 L8 5 L1 5 Z" />
      <path className={'rt-sort__tri' + (dir === 'desc' ? ' is-on' : '')} d="M4.5 11 L8 7 L1 7 Z" />
    </svg>
  );
}

export function ColHead({ col, state, rowSpan }) {
  const { sort, toggleSort, filters, setFilter, distinctFor } = state;
  const sorted = sort && sort.key === col.key ? sort.dir : null;
  const fActive = !!filters[col.key];
  const alignCls = col.align === 'num' ? ' rt-th--num' : col.align === 'center' ? ' rt-th--center' : '';
  // label2 — управляемый перенос на РОВНО две строки (обе nowrap: колонка расширяется
  // под самую длинную строку, текст не режется трёхстрочным переносом)
  const label = (
    <span className="rt-th__label" title={col.title}>
      {col.label2
        ? <><span className="rt-th__line">{col.label}</span><span className="rt-th__line">{col.label2}</span></>
        : col.label}
    </span>
  );
  const distinctCol = col.type === 'enum' ? { ...col, _distinct: distinctFor(col) } : col;
  const fullLabel = col.label + (col.label2 ? ' ' + col.label2 : '');
  return (
    <th className={col.thClass} rowSpan={rowSpan}>
      <div className={'rt-th' + alignCls}>
        {col.sortable === false ? label : (
          <button type="button" className={'rt-th__sort' + (sorted ? ' is-sorted' : '')}
            onClick={() => toggleSort(col.key)}
            aria-label={'Сортировать: ' + fullLabel} title="Сортировать">
            {label}
            <SortIcon dir={sorted} />
          </button>
        )}
        {col.filterable === false ? null : (
          <Popover placement={col.align === 'num' ? 'bottom-end' : 'bottom-start'}
            width={col.type === 'text' ? 220 : col.type === 'num' ? 200 : 200}
            trigger={(
              <button type="button" className={'rt-th__filter' + (fActive ? ' is-active' : '')}
                aria-label={'Фильтр: ' + fullLabel} title="Фильтр">
                <Icon name="filter" size={12} />
              </button>
            )}>
            <ColFilter col={distinctCol} value={filters[col.key]} onChange={(v) => setFilter(col.key, v)} />
          </Popover>
        )}
      </div>
      {/* Ручка ширины столбца (ColumnWidths.jsx): рисуется, только если таблица
          подключена (ResponsiveTable с пропом widths + TableCols в таблице), и не
          в карточке. */}
      <ColResize col={col} />
    </th>
  );
}
